import { Globals } from '../globalfunctions.js';

export const ROLES = {
  SYSADMIN: 'sysadmin',
  SUPER_ADMIN: 'Sysadmin',
  ADMIN: 'admin', 
  MANAGER: 'manager',
  STANDARD: 'standard',
  READONLY: 'readonly',
  CUSTOMER: 'customer'
};

export const PERMISSIONS = {
  // User management
  CREATE_USERS: 'create_users',
  EDIT_USERS: 'edit_users',
  DELETE_USERS: 'delete_users',
  VIEW_USERS: 'view_users',
  MANAGE_ROLES: 'manage_roles',
  IMPERSONATE_USERS: 'impersonate_users',
  
  // Invoice management
  CREATE_INVOICES: 'create_invoices',
  EDIT_INVOICES: 'edit_invoices',
  DELETE_INVOICES: 'delete_invoices',
  VIEW_INVOICES: 'view_invoices',
  APPROVE_INVOICES: 'approve_invoices',
  
  // Customer management
  CREATE_CUSTOMERS: 'create_customers',
  EDIT_CUSTOMERS: 'edit_customers',
  DELETE_CUSTOMERS: 'delete_customers',
  VIEW_CUSTOMERS: 'view_customers',
  
  // Financial data
  VIEW_SALES_DATA: 'view_sales_data',
  VIEW_FINANCIAL_REPORTS: 'view_financial_reports',
  EXPORT_DATA: 'export_data',
  
  // System settings
  MANAGE_SETTINGS: 'manage_settings',
  VIEW_AUDIT_LOGS: 'view_audit_logs',
  MANAGE_BACKUPS: 'manage_backups',
  MANAGE_MAIL_SETTINGS: 'manage_mail_settings',
  
  // 2FA Management
  RESET_USER_2FA: 'reset_user_2fa',
  MANAGE_2FA_SETTINGS: 'manage_2fa_settings',
  
  // Recurring billing
  VIEW_SUBSCRIPTIONS: 'view_subscriptions',
  MANAGE_SUBSCRIPTIONS: 'manage_subscriptions',

  // Customer tags / segments
  MANAGE_TAGS: 'manage_tags',

  // Uptime / outage tracking
  VIEW_UPTIME: 'view_uptime',

  // cPanel resource monitoring (sysadmin-only - see ROLE_PERMISSIONS below,
  // this is deliberately left out of every role but sysadmin/super admin)
  VIEW_CPANEL: 'view_cpanel',

  // Hosting account management - creating/suspending/linking real cPanel
  // accounts is sysadmin-only on the backend (requireSysadmin), so this is
  // left out of every role but sysadmin/super admin, same as VIEW_CPANEL.
  MANAGE_HOSTING: 'manage_hosting',

  // Quotes / estimates
  VIEW_QUOTES: 'view_quotes',
  MANAGE_QUOTES: 'manage_quotes',
  CONVERT_QUOTES: 'convert_quotes',

  // Items/Products
  CREATE_ITEMS: 'create_items',
  EDIT_ITEMS: 'edit_items',
  DELETE_ITEMS: 'delete_items',
  VIEW_ITEMS: 'view_items',
  
  // Tickets/Support
  CREATE_TICKETS: 'create_tickets',
  EDIT_TICKETS: 'edit_tickets',
  DELETE_TICKETS: 'delete_tickets',
  VIEW_TICKETS: 'view_tickets',
  ASSIGN_TICKETS: 'assign_tickets',

  // Leads
  CREATE_LEADS: 'create_leads',
  EDIT_LEADS: 'edit_leads',
  DELETE_LEADS: 'delete_leads',
  VIEW_LEADS: 'view_leads',
  ASSIGN_LEADS: 'assign_leads',
  CONVERT_LEADS: 'convert_leads'
};

const ROLE_PERMISSIONS = {
  [ROLES.SYSADMIN]: Object.values(PERMISSIONS),
  [ROLES.SUPER_ADMIN]: Object.values(PERMISSIONS),
  
  [ROLES.ADMIN]: [
    PERMISSIONS.CREATE_USERS,
    PERMISSIONS.EDIT_USERS,
    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.IMPERSONATE_USERS,
    PERMISSIONS.CREATE_INVOICES,
    PERMISSIONS.EDIT_INVOICES,
    PERMISSIONS.DELETE_INVOICES,
    PERMISSIONS.VIEW_INVOICES,
    PERMISSIONS.APPROVE_INVOICES,
    PERMISSIONS.CREATE_CUSTOMERS,
    PERMISSIONS.EDIT_CUSTOMERS,
    PERMISSIONS.DELETE_CUSTOMERS,
    PERMISSIONS.VIEW_CUSTOMERS,
    PERMISSIONS.VIEW_SALES_DATA,
    PERMISSIONS.VIEW_FINANCIAL_REPORTS,
    PERMISSIONS.EXPORT_DATA,
    PERMISSIONS.MANAGE_SETTINGS,
    PERMISSIONS.MANAGE_MAIL_SETTINGS,
    PERMISSIONS.VIEW_AUDIT_LOGS,
    PERMISSIONS.VIEW_SUBSCRIPTIONS,
    PERMISSIONS.MANAGE_SUBSCRIPTIONS,
    PERMISSIONS.VIEW_QUOTES,
    PERMISSIONS.MANAGE_QUOTES,
    PERMISSIONS.CONVERT_QUOTES,
    PERMISSIONS.MANAGE_TAGS,
    PERMISSIONS.VIEW_UPTIME,
    PERMISSIONS.RESET_USER_2FA,
    PERMISSIONS.MANAGE_2FA_SETTINGS,
    PERMISSIONS.CREATE_ITEMS,
    PERMISSIONS.EDIT_ITEMS,
    PERMISSIONS.DELETE_ITEMS,
    PERMISSIONS.VIEW_ITEMS,
    PERMISSIONS.CREATE_TICKETS,
    PERMISSIONS.EDIT_TICKETS,
    PERMISSIONS.DELETE_TICKETS,
    PERMISSIONS.VIEW_TICKETS,
    PERMISSIONS.ASSIGN_TICKETS,
    PERMISSIONS.CREATE_LEADS,
    PERMISSIONS.EDIT_LEADS,
    PERMISSIONS.DELETE_LEADS,
    PERMISSIONS.VIEW_LEADS,
    PERMISSIONS.ASSIGN_LEADS,
    PERMISSIONS.CONVERT_LEADS
  ],

  [ROLES.MANAGER]: [
    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.CREATE_INVOICES,
    PERMISSIONS.EDIT_INVOICES,
    PERMISSIONS.VIEW_INVOICES,
    PERMISSIONS.APPROVE_INVOICES,
    PERMISSIONS.CREATE_CUSTOMERS,
    PERMISSIONS.EDIT_CUSTOMERS,
    PERMISSIONS.VIEW_CUSTOMERS,
    PERMISSIONS.VIEW_SALES_DATA,
    PERMISSIONS.VIEW_FINANCIAL_REPORTS,
    PERMISSIONS.EXPORT_DATA,
    PERMISSIONS.VIEW_SUBSCRIPTIONS,
    PERMISSIONS.MANAGE_SUBSCRIPTIONS,
    PERMISSIONS.VIEW_QUOTES,
    PERMISSIONS.MANAGE_QUOTES,
    PERMISSIONS.CONVERT_QUOTES,
    PERMISSIONS.MANAGE_TAGS,
    PERMISSIONS.VIEW_UPTIME,
    PERMISSIONS.CREATE_ITEMS,
    PERMISSIONS.EDIT_ITEMS,
    PERMISSIONS.VIEW_ITEMS,
    PERMISSIONS.CREATE_TICKETS,
    PERMISSIONS.EDIT_TICKETS,
    PERMISSIONS.VIEW_TICKETS,
    PERMISSIONS.ASSIGN_TICKETS,
    PERMISSIONS.CREATE_LEADS,
    PERMISSIONS.EDIT_LEADS,
    PERMISSIONS.VIEW_LEADS,
    PERMISSIONS.ASSIGN_LEADS,
    PERMISSIONS.CONVERT_LEADS
  ],

  [ROLES.STANDARD]: [
    PERMISSIONS.CREATE_INVOICES,
    PERMISSIONS.EDIT_INVOICES,
    PERMISSIONS.VIEW_INVOICES,
    PERMISSIONS.CREATE_CUSTOMERS,
    PERMISSIONS.EDIT_CUSTOMERS,
    PERMISSIONS.VIEW_CUSTOMERS,
    PERMISSIONS.VIEW_SALES_DATA,
    PERMISSIONS.VIEW_QUOTES,
    PERMISSIONS.MANAGE_QUOTES,
    PERMISSIONS.CONVERT_QUOTES,
    PERMISSIONS.VIEW_UPTIME,
    PERMISSIONS.CREATE_ITEMS,
    PERMISSIONS.EDIT_ITEMS,
    PERMISSIONS.VIEW_ITEMS,
    PERMISSIONS.CREATE_LEADS,
    PERMISSIONS.EDIT_LEADS,
    PERMISSIONS.VIEW_LEADS,
    PERMISSIONS.CONVERT_LEADS
  ],

  [ROLES.READONLY]: [
    PERMISSIONS.VIEW_INVOICES,
    PERMISSIONS.VIEW_CUSTOMERS,
    PERMISSIONS.VIEW_ITEMS,
    PERMISSIONS.VIEW_LEADS,
    PERMISSIONS.VIEW_QUOTES
  ],

  [ROLES.CUSTOMER]: [

  ]
};

class RoleManager {
  getCurrentUserRole() {
    return Globals.UserType || ROLES.READONLY;
  }

  hasPermission(permission) {
    const userRole = this.getCurrentUserRole();
    const userPermissions = ROLE_PERMISSIONS[userRole] || [];
    return userPermissions.includes(permission);
  }

  hasAnyPermission(permissions) {
    return permissions.some(permission => this.hasPermission(permission));
  }

  hasAllPermissions(permissions) {
    return permissions.every(permission => this.hasPermission(permission));
  }

  canAccessRoute(routePath) {
    const routePermissions = {
      '/users': [PERMISSIONS.VIEW_USERS],
      '/createcustomer': [PERMISSIONS.CREATE_CUSTOMERS],
      '/addinvoice': [PERMISSIONS.CREATE_INVOICES],
      '/invoices': [PERMISSIONS.VIEW_INVOICES],
      '/sales': [PERMISSIONS.VIEW_SALES_DATA],
      '/items': [PERMISSIONS.VIEW_ITEMS],
      '/settings': [PERMISSIONS.MANAGE_SETTINGS],
      '/mail-settings': [PERMISSIONS.MANAGE_MAIL_SETTINGS],
      '/transactions': [PERMISSIONS.VIEW_FINANCIAL_REPORTS],
      '/leads': [PERMISSIONS.VIEW_LEADS],
      '/audit-logs': [PERMISSIONS.VIEW_AUDIT_LOGS],
      '/subscriptions': [PERMISSIONS.VIEW_SUBSCRIPTIONS],
      '/quotes': [PERMISSIONS.VIEW_QUOTES],
      '/uptime': [PERMISSIONS.VIEW_UPTIME],
      '/cpanel': [PERMISSIONS.VIEW_CPANEL],
      '/hosting': [PERMISSIONS.MANAGE_HOSTING]
    };

    const requiredPermissions = routePermissions[routePath];
    if (!requiredPermissions) return true; // Allow access to unprotected routes
    
    return this.hasAnyPermission(requiredPermissions);
  }

  getUserPermissions() {
    const userRole = this.getCurrentUserRole();
    return ROLE_PERMISSIONS[userRole] || [];
  }

  getRoleHierarchy() {
    return {
      [ROLES.SYSADMIN]: 6,
      [ROLES.SUPER_ADMIN]: 5,
      [ROLES.ADMIN]: 4,
      [ROLES.MANAGER]: 3,
      [ROLES.STANDARD]: 2,
      [ROLES.READONLY]: 1
    };
  }

  isHigherRole(compareRole) {
    const hierarchy = this.getRoleHierarchy();
    const currentLevel = hierarchy[this.getCurrentUserRole()] || 0;
    const compareLevel = hierarchy[compareRole] || 0;
    return currentLevel > compareLevel;
  }

  canManageUser(targetUserRole) {
    // Super admin can manage anyone
    if (this.getCurrentUserRole() === ROLES.SYSADMIN) return true;
    
    // Admin can manage manager and below
    if (this.getCurrentUserRole() === ROLES.ADMIN) {
      return [ROLES.MANAGER, ROLES.STANDARD, ROLES.READONLY].includes(targetUserRole);
    }
    
    // Manager can manage standard and readonly
    if (this.getCurrentUserRole() === ROLES.MANAGER) {
      return [ROLES.STANDARD, ROLES.READONLY].includes(targetUserRole);
    }
    
    return false;
  }

  getVisibleNavigationItems() {
    const items = [];
    
    if (this.hasPermission(PERMISSIONS.VIEW_INVOICES)) {
      const invoicesSubmenu = [
        { path: '/invoices', label: 'All Invoices', icon: 'fas fa-file-invoice' }
      ];
      if (this.hasPermission(PERMISSIONS.CREATE_INVOICES)) {
        invoicesSubmenu.push({ path: '/addinvoice', label: 'Add New', icon: 'fas fa-plus-circle' });
      }
      items.push({ path: '/invoices', label: 'Invoices', icon: 'fas fa-file-invoice', submenu: invoicesSubmenu });
    }

    if (this.hasPermission(PERMISSIONS.VIEW_QUOTES)) {
      items.push({
        path: '/quotes',
        label: 'Quotes',
        icon: 'fas fa-file-signature',
        submenu: [{ path: '/quotes', label: 'View Quotes', icon: 'fas fa-file-signature' }]
      });
    }

    if (this.hasPermission(PERMISSIONS.VIEW_CUSTOMERS)) {
      const customersSubmenu = [
        { path: '/users', label: 'All Customers', icon: 'fas fa-users' }
      ];
      if (this.hasPermission(PERMISSIONS.CREATE_CUSTOMERS)) {
        customersSubmenu.push({ path: '/createcustomer', label: 'Add a Customer', icon: 'fas fa-user-plus' });
      }
      items.push({ path: '/users', label: 'Customers', icon: 'fas fa-users', submenu: customersSubmenu });
    }
    
    if (this.hasPermission(PERMISSIONS.VIEW_FINANCIAL_REPORTS)) {
      items.push({
        path: '/transactions',
        label: 'Transactions',
        icon: 'fas fa-money-bill-wave',
        submenu: [{ path: '/transactions', label: 'View Transactions', icon: 'fas fa-money-bill-wave' }]
      });
    }

    if (this.hasPermission(PERMISSIONS.VIEW_ITEMS)) {
      // The previous "Add New Item" entry pointed at the same /items path
      // as its parent (a dead end - no create form exists), so it's left
      // out rather than kept as a non-functional link.
      items.push({
        path: '/items',
        label: 'Items',
        icon: 'fas fa-box',
        submenu: [{ path: '/items', label: 'View Items', icon: 'fas fa-box' }]
      });
    }

    if (this.hasPermission(PERMISSIONS.VIEW_SALES_DATA)) {
      items.push({
        path: '/sales',
        label: 'Sales Rocket',
        icon: 'fas fa-chart-pie',
        submenu: [{ path: '/sales', label: 'View Dashboard', icon: 'fas fa-chart-pie' }]
      });
    }

    if (this.hasPermission(PERMISSIONS.VIEW_TICKETS)) {
      const ticketsSubmenu = [
        { path: '/tickets', label: 'Ticket Overview', icon: 'fas fa-ticket' }
      ];
      if (this.hasPermission(PERMISSIONS.MANAGE_MAIL_SETTINGS)) {
        ticketsSubmenu.push({ path: '/mail-settings', label: 'Inbox Settings', icon: 'fas fa-inbox' });
        ticketsSubmenu.push({ path: '/mail-blocklist', label: 'Whitelist / Blacklist', icon: 'fas fa-ban' });
        ticketsSubmenu.push({ path: '/ticket-rules', label: 'Ticket Rules', icon: 'fas fa-wand-magic-sparkles' });
        ticketsSubmenu.push({ path: '/ticket-hours', label: 'SLA & Hours', icon: 'fas fa-business-time' });
      }
      ticketsSubmenu.push({ path: '/knowledge-base', label: 'Knowledge Base', icon: 'fas fa-book-open' });
      ticketsSubmenu.push({ path: '/ticket-stats', label: 'Stats', icon: 'fas fa-chart-line' });
      items.push({ path: '/tickets', label: 'Tickets', icon: 'fas fa-ticket', submenu: ticketsSubmenu });
    }

    if (this.hasPermission(PERMISSIONS.VIEW_LEADS)) {
      items.push({
        path: '/leads',
        label: 'Leads',
        icon: 'fas fa-bullseye',
        submenu: [
          { path: '/leads', label: 'View Leads', icon: 'fas fa-bullseye' },
          { path: '/lead-stats', label: 'Stats', icon: 'fas fa-chart-line' }
        ]
      });
    }

    if (this.hasPermission(PERMISSIONS.VIEW_SUBSCRIPTIONS)) {
      items.push({
        path: '/subscriptions',
        label: 'Subscriptions',
        icon: 'fas fa-rotate',
        submenu: [{ path: '/subscriptions', label: 'View Subscriptions', icon: 'fas fa-rotate' }]
      });
    }

    // Outages, cPanel and Hosting are all "keep the infrastructure healthy"
    // tools rather than day-to-day CRM work, so they share one flyout
    // instead of each claiming their own top-level slot.
    const opsSubmenu = [];
    if (this.hasPermission(PERMISSIONS.VIEW_UPTIME)) {
      opsSubmenu.push({ path: '/uptime', label: 'Outages', icon: 'fas fa-heart-pulse' });
    }
    if (this.hasPermission(PERMISSIONS.VIEW_CPANEL)) {
      opsSubmenu.push({ path: '/cpanel', label: 'cPanel', icon: 'fas fa-server' });
    }
    if (this.hasPermission(PERMISSIONS.MANAGE_HOSTING)) {
      opsSubmenu.push({ path: '/hosting', label: 'Hosting', icon: 'fas fa-hard-drive' });
    }
    if (opsSubmenu.length > 0) {
      items.push({
        path: opsSubmenu[0].path,
        label: 'Operational Dashboard',
        icon: 'fas fa-gauge-high',
        submenu: opsSubmenu
      });
    }

    if (this.hasPermission(PERMISSIONS.VIEW_AUDIT_LOGS)) {
      items.push({
        path: '/audit-logs',
        label: 'Audit Log',
        icon: 'fas fa-clipboard-list',
        submenu: [{ path: '/audit-logs', label: 'View Audit Log', icon: 'fas fa-clipboard-list' }]
      });
    }

    return items;
  }
}

const roleManager = new RoleManager();
export default roleManager;